"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_FOOTBALL_COMPETITION, SITE_TYPE_BASIC, SITE_TYPE_FOOTBALL_TEAM } from "@/config/siteTypes";
import { useSearchPage } from "./useSearchPage";
import { SearchBar } from "./SearchBar";
import { EntitySection } from "./EntitySection";
import { ArticleResultList } from "./ArticleResultList";
import { EmptyState } from "./EmptyState";
interface SearchPageDefaultProps {
  locale: string;
}
export function SearchPageDefault(_) {
  const $ = _c(71);
  const labels = useLabels();
  const {
    siteType
  } = useTenantSettings();
  const isCompetitionSite = siteType === SITE_TYPE_FOOTBALL_COMPETITION;
  const isBasic = siteType === SITE_TYPE_BASIC;
  const isFootballTeam = siteType === SITE_TYPE_FOOTBALL_TEAM;
  const skipEntities = isBasic || isFootballTeam;
  const {
    query,
    setQuery,
    teams,
    players,
    competitions,
    entitiesLoading,
    articles,
    articlesTotal,
    articlesLoading,
    articlesLoadingMore,
    hasNextArticlesPage,
    articlesError,
    loadMoreArticles,
    retryArticles
  } = useSearchPage(skipEntities);
  let t0;
  if ($[0] !== query) {
    t0 = query.trim();
    $[0] = query;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trimmedQuery = t0;
  const isSearching = trimmedQuery.length >= 2;
  const isLoading = entitiesLoading || articlesLoading;
  let t1;
  if ($[2] !== competitions || $[3] !== isCompetitionSite || $[4] !== skipEntities) {
    t1 = isCompetitionSite || skipEntities ? [] : competitions;
    $[2] = competitions;
    $[3] = isCompetitionSite;
    $[4] = skipEntities;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const visibleCompetitions = t1;
  let t2;
  if ($[6] !== skipEntities || $[7] !== teams) {
    t2 = skipEntities ? [] : teams;
    $[6] = skipEntities;
    $[7] = teams;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const visibleTeams = t2;
  let t3;
  if ($[9] !== players || $[10] !== skipEntities) {
    t3 = skipEntities ? [] : players;
    $[9] = players;
    $[10] = skipEntities;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const visiblePlayers = t3;
  const hasAnyEntity = visibleTeams.length + visiblePlayers.length + visibleCompetitions.length > 0;
  const showInitialState = !isSearching;
  const showLoadingState = isSearching && isLoading && !hasAnyEntity && articles.length === 0;
  const showNoResults = isSearching && !isLoading && !hasAnyEntity && articles.length === 0 && !articlesError;
  let t4;
  bb0: {
    if (!isSearching || isLoading) {
      t4 = null;
      break bb0;
    }
    if (!hasAnyEntity && articles.length === 0) {
      t4 = null;
      break bb0;
    }
    const template = labels.search?.countSummary || "{teams} teams \xB7 {players} players \xB7 {competitions} competitions \xB7 {articles} articles";
    let parts;
    if ($[12] !== articlesTotal || $[13] !== labels.search?.articles || $[14] !== labels.search?.competitions || $[15] !== labels.search?.players || $[16] !== labels.search?.teams || $[17] !== visibleCompetitions.length || $[18] !== visiblePlayers.length || $[19] !== visibleTeams.length) {
      parts = [];
      if (visibleTeams.length > 0) {
        let t5;
        if ($[21] !== labels.search?.teams) {
          t5 = labels.search?.teams?.toLowerCase() || "teams";
          $[21] = labels.search?.teams;
          $[22] = t5;
        } else {
          t5 = $[22];
        }
        parts.push(`${visibleTeams.length} ${t5}`);
      }
      if (visiblePlayers.length > 0) {
        let t5;
        if ($[23] !== labels.search?.players) {
          t5 = labels.search?.players?.toLowerCase() || "players";
          $[23] = labels.search?.players;
          $[24] = t5;
        } else {
          t5 = $[24];
        }
        parts.push(`${visiblePlayers.length} ${t5}`);
      }
      if (visibleCompetitions.length > 0) {
        let t5;
        if ($[25] !== labels.search?.competitions) {
          t5 = labels.search?.competitions?.toLowerCase() || "competitions";
          $[25] = labels.search?.competitions;
          $[26] = t5;
        } else {
          t5 = $[26];
        }
        parts.push(`${visibleCompetitions.length} ${t5}`);
      }
      if (articlesTotal > 0) {
        let t5;
        if ($[27] !== labels.search?.articles) {
          t5 = labels.search?.articles?.toLowerCase() || "articles";
          $[27] = labels.search?.articles;
          $[28] = t5;
        } else {
          t5 = $[28];
        }
        parts.push(`${articlesTotal} ${t5}`);
      }
      $[12] = articlesTotal;
      $[13] = labels.search?.articles;
      $[14] = labels.search?.competitions;
      $[15] = labels.search?.players;
      $[16] = labels.search?.teams;
      $[17] = visibleCompetitions.length;
      $[18] = visiblePlayers.length;
      $[19] = visibleTeams.length;
      $[20] = parts;
    } else {
      parts = $[20];
    }
    if (parts.length === 0) {
      t4 = null;
      break bb0;
    }
    const forText = ` ${labels.search?.forQuery || "for"} "${trimmedQuery}"`;
    if (template.includes("{teams}") && template.includes("{articles}")) {
      let t5;
      if ($[29] !== articlesTotal || $[30] !== template || $[31] !== visibleCompetitions.length || $[32] !== visiblePlayers.length || $[33] !== visibleTeams.length) {
        t5 = template.replace("{teams}", String(visibleTeams.length)).replace("{players}", String(visiblePlayers.length)).replace("{competitions}", String(visibleCompetitions.length)).replace("{articles}", String(articlesTotal));
        $[29] = articlesTotal;
        $[30] = template;
        $[31] = visibleCompetitions.length;
        $[32] = visiblePlayers.length;
        $[33] = visibleTeams.length;
        $[34] = t5;
      } else {
        t5 = $[34];
      }
      t4 = t5 + forText;
      break bb0;
    }
    t4 = parts.join(" \xB7 ") + forText;
  }
  const countSummary = t4;
  let t5;
  if ($[35] !== countSummary || $[36] !== isLoading || $[37] !== query || $[38] !== setQuery) {
    t5 = <SearchBar query={query} onChange={setQuery} loading={isLoading} countSummary={countSummary} />;
    $[35] = countSummary;
    $[36] = isLoading;
    $[37] = query;
    $[38] = setQuery;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== setQuery || $[41] !== showInitialState) {
    t6 = showInitialState && <EmptyState variant="initial" onSuggestionClick={setQuery} />;
    $[40] = setQuery;
    $[41] = showInitialState;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== showLoadingState) {
    t7 = showLoadingState && <EmptyState variant="loading" />;
    $[43] = showLoadingState;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  let t8;
  if ($[45] !== showNoResults || $[46] !== trimmedQuery) {
    t8 = showNoResults && <EmptyState variant="no-results" query={trimmedQuery} />;
    $[45] = showNoResults;
    $[46] = trimmedQuery;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  let t9;
  if ($[48] !== articles || $[49] !== articlesError || $[50] !== articlesLoadingMore || $[51] !== articlesTotal || $[52] !== hasNextArticlesPage || $[53] !== isSearching || $[54] !== labels.search?.articles || $[55] !== labels.search?.competitions || $[56] !== labels.search?.players || $[57] !== labels.search?.teams || $[58] !== loadMoreArticles || $[59] !== retryArticles || $[60] !== showLoadingState || $[61] !== visibleCompetitions || $[62] !== visiblePlayers || $[63] !== visibleTeams) {
    t9 = isSearching && !showLoadingState && <>{visibleTeams.length > 0 && <EntitySection title={labels.search?.teams || "Teams"} items={visibleTeams} kind="teams" />}{visiblePlayers.length > 0 && <EntitySection title={labels.search?.players || "Players"} items={visiblePlayers} kind="players" />}{visibleCompetitions.length > 0 && <EntitySection title={labels.search?.competitions || "Competitions"} items={visibleCompetitions} kind="competitions" />}<ArticleResultList title={labels.search?.articles || "Articles"} articles={articles} total={articlesTotal} loadingMore={articlesLoadingMore} hasNextPage={hasNextArticlesPage} error={articlesError && articles.length === 0} onLoadMore={loadMoreArticles} onRetry={retryArticles} /></>;
    $[48] = articles;
    $[49] = articlesError;
    $[50] = articlesLoadingMore;
    $[51] = articlesTotal;
    $[52] = hasNextArticlesPage;
    $[53] = isSearching;
    $[54] = labels.search?.articles;
    $[55] = labels.search?.competitions;
    $[56] = labels.search?.players;
    $[57] = labels.search?.teams;
    $[58] = loadMoreArticles;
    $[59] = retryArticles;
    $[60] = showLoadingState;
    $[61] = visibleCompetitions;
    $[62] = visiblePlayers;
    $[63] = visibleTeams;
    $[64] = t9;
  } else {
    t9 = $[64];
  }
  let t10;
  if ($[65] !== t5 || $[66] !== t6 || $[67] !== t7 || $[68] !== t8 || $[69] !== t9) {
    t10 = <div className="container mx-auto px-4 py-8 max-w-4xl min-h-[60vh]">{t5}{t6}{t7}{t8}{t9}</div>;
    $[65] = t5;
    $[66] = t6;
    $[67] = t7;
    $[68] = t8;
    $[69] = t9;
    $[70] = t10;
  } else {
    t10 = $[70];
  }
  return t10;
}